import type { Theme } from '@mui/material';
import type { ChartBlock, CalloutBlock, Metric } from '../../../types/backend';

type ValueFormat = NonNullable<ChartBlock['value_format']>;

/**
 * Numeric value formatter shared by chart axes, tooltips and screen-reader
 * summaries. This is an independent, stylistically-tuned approximation of the
 * same `value_format` vocabulary the server-rendered report uses — chat formats
 * via MUI charts, the report via server SVG. The bytes/currency/duration/percent
 * outputs intentionally differ between the two and are not byte-identical.
 */
export function makeValueFormatter(
  fmt?: ChartBlock['value_format'],
): (v: number | null) => string {
  switch (fmt) {
    case 'currency':
      return (v) =>
        v == null
          ? '—'
          : v >= 1000
            ? `$${(v / 1000).toFixed(1)}K`
            : `$${v.toFixed(0)}`;
    case 'percent':
      return (v) => (v == null ? '—' : `${v}%`);
    case 'bytes':
      return (v) =>
        v == null
          ? '—'
          : v >= 1073741824
            ? `${(v / 1073741824).toFixed(1)} GB`
            : v >= 1048576
              ? `${(v / 1048576).toFixed(1)} MB`
              : `${(v / 1024).toFixed(1)} KB`;
    case 'duration':
      return (v) =>
        v == null
          ? '—'
          : v >= 3600
            ? `${(v / 3600).toFixed(1)}h`
            : v >= 60
              ? `${(v / 60).toFixed(1)}m`
              : `${v}s`;
    default:
      return (v) => (v == null ? '—' : v.toLocaleString());
  }
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  DKK: 'kr',
};

export function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

export function formatCurrency(value: number, currency: string): string {
  const symbol = currencySymbol(currency);
  if (value >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${symbol}${(value / 1_000).toFixed(1)}K`;
  return `${symbol}${value.toFixed(2)}`;
}

/**
 * Ordered, theme-derived categorical palette for multi-series charts, pie slices
 * and cost bars. Stays on-brand and theme-aware (no raw hex) by pulling from the
 * active MUI palette rather than a hardcoded ramp.
 */
export function chartPalette(theme: Theme): string[] {
  const p = theme.palette;
  return [
    p.primary.main,
    p.secondary.main,
    p.success.main,
    p.warning.main,
    p.info.main,
    p.error.main,
    p.primary.light,
    p.secondary.dark,
  ];
}

const CALLOUT_SEVERITY: Record<CalloutBlock['variant'], 'info' | 'success' | 'warning' | 'error'> = {
  info: 'info',
  ok: 'success',
  warn: 'warning',
  err: 'error',
};

export function calloutSeverity(
  variant: CalloutBlock['variant'],
): 'info' | 'success' | 'warning' | 'error' {
  return CALLOUT_SEVERITY[variant];
}

export const TREND_GLYPH: Record<NonNullable<Metric['trend']>, string> = {
  up: '↑',
  down: '↓',
  flat: '→',
};

export type { ValueFormat };
